/* eslint-disable no-console */
import {Col, Form, Input, Select} from 'antd';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import React, {useContext, useEffect, useMemo, useState} from 'react';

import {ReactComponent as YearCalender} from '../../assets/yearCalender.svg';
import {CommonErrors, TF} from '../../constants/Common';
import FilteredUnit from '../../constants/FilteredUnit';
import SelectedCountryContext from '../../pages/configurator/orgFacility/Constants/SelectedCountryContext';
import {sortYearOptions} from '../../pages/orgManager/Utils/InfraStructureProfile/constant/sorYear';
import NumberInput from '../../shared/NumberInput';
import yearFilterArray from '../../shared/YearFilterArray';
import useCompanyStore from '../../store/companyStore/companyDetailsStore';
import useJSONStore from '../../store/StaticJson/jsonStore';
import useUserStore from '../../store/userStore/userStore';
import {hasUserRole, UserRole} from '../../utils/commonFunctions';
import {eachWordCap} from '../../utils/FormUtils/FirstCap';
import RegEx from '../../utils/FormUtils/RegEx';
import {MYConfig, MYData} from '../MonthlyYearly/Interface/common';
import MonthlyYearlyDrawerForm from '../MonthlyYearly/MonthlyYearlyDrawerForm';

import {facilityKeyLabel, yearKeyLabel} from './Constants/IP/commonKeyLabel';
import {RenewableKey, RenewableLabel} from './Constants/IP/RenewableKeyLabel';

const RenewableForm: React.FC<{
  currentStep?: number;
  defaultDisplayStep?: boolean;
}> = ({currentStep, defaultDisplayStep = false}) => {
  const isCompanyUserOrAdmin =
    hasUserRole(UserRole.Company_Admin) || hasUserRole(UserRole.Company_user);
  const {getFieldValue, setFieldValue, setFields} = useFormInstance();
  const userDetails = useUserStore.getState().userDetails;
  const renewableType = useJSONStore.getState().renewableType;
  const {facilityId, selectedYear, selectedCountry} = useContext(
    SelectedCountryContext
  );

  const facilityDetails = useCompanyStore.getState().facilityDetails;
  const [formSelectedCountry, setFormSelectedCountry] = useState<string>('');
  const [facilityOpt, setFacilityOpt] = useState<any[]>([]);

  useEffect(() => {
    setFacilityOpt(
      facilityDetails?.[
        getFieldValue(yearKeyLabel.key) ?? selectedYear ?? ''
      ] ?? []
    );
  }, []);

  useEffect(() => {
    setFormSelectedCountry(
      facilityOpt?.find((item: any) => {
        return (
          item?._id === (getFieldValue(facilityKeyLabel.key) ?? facilityId)
        );
      })?.country
    );
  }, [facilityOpt]);

  const [yearOptions, setYearOptions] = useState<any[]>([]);

  useEffect(() => {
    setYearOptions(
      yearFilterArray(
        facilityDetails,
        !isCompanyUserOrAdmin ? userDetails?.facility : undefined
      )
    );
  }, []);

  useEffect(() => {
    setFieldValue(
      RenewableKey.CAPACITY_UNIT,
      FilteredUnit(formSelectedCountry ?? selectedCountry)?.electricity
    );
  }, [formSelectedCountry, selectedCountry]);

  const monYear: MYConfig[] = useMemo(() => {
    return [
      {
        mainTitle: {
          key: RenewableKey.SELF_GENERATED_TF,
          title: RenewableLabel.SELF_GENERATED_TF
        },
        unit: FilteredUnit(formSelectedCountry ?? selectedCountry)?.electricity
      },
      {
        mainTitle: {
          key: RenewableKey.PURCHASED_TF,
          title: RenewableLabel.PURCHASED_TF
        },
        unit: FilteredUnit(formSelectedCountry ?? selectedCountry)?.electricity
      },
      {
        mainTitle: {
          key: RenewableKey.PURCHASED_COST_TF,
          title: RenewableLabel.PURCHASED_COST_TF
        },
        unit: FilteredUnit(formSelectedCountry ?? selectedCountry)?.currency
      }
    ];
  }, [formSelectedCountry, selectedCountry]);

  return (
    <>
      {(currentStep === 0 || defaultDisplayStep) && (
        <>
          <Col className="gutter-row">
            <Form.Item
              name={yearKeyLabel.key}
              label={yearKeyLabel.label}
              labelCol={{span: 24}}
              initialValue={selectedYear}
              hidden={defaultDisplayStep}
            >
              <Select
                placeholder={yearKeyLabel.label}
                options={sortYearOptions(yearOptions)}
                suffixIcon={<YearCalender />}
                disabled={getFieldValue('_id') !== undefined}
                onChange={(e) => {
                  setFacilityOpt(facilityDetails?.[e] ?? []);
                  if (getFieldValue(facilityKeyLabel.key)) {
                    setFields([
                      {
                        name: facilityKeyLabel.key,
                        value: undefined,
                        errors: ['Please select facility again']
                      }
                    ]);
                  }
                }}
              />
            </Form.Item>
          </Col>
          {isCompanyUserOrAdmin && (
            <>
              <Col className="gutter-row">
                <Form.Item
                  name={facilityKeyLabel.key}
                  label={facilityKeyLabel.label}
                  hidden={defaultDisplayStep}
                  labelCol={{span: 24}}
                  rules={[
                    {
                      required: true,
                      message: CommonErrors.mandatory(facilityKeyLabel.label)
                    }
                  ]}
                  initialValue={facilityId}
                >
                  <Select
                    placeholder={facilityKeyLabel.label}
                    options={
                      facilityOpt?.map((item: any) => {
                        return {label: item.name, value: item._id};
                      }) ?? []
                    }
                    disabled={getFieldValue('_id') !== undefined}
                    onChange={(e) => {
                      setFormSelectedCountry(
                        facilityDetails?.[
                          getFieldValue(yearKeyLabel.key) ?? ''
                        ]?.find((item: any) => {
                          return item._id === e;
                        })?.country
                      );
                    }}
                  />
                </Form.Item>
              </Col>
            </>
          )}
        </>
      )}
      {(currentStep === 1 || defaultDisplayStep) && (
        <>
          <Col className="gutter-row">
            <Form.Item
              name={RenewableKey.TYPE}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(RenewableLabel.TYPE)
                }
              ]}
              label={RenewableLabel.TYPE}
              labelCol={{span: 24}}
              validateFirst
              hasFeedback
            >
              <Select
                className="select"
                placeholder={`Choose ${RenewableLabel.TYPE}`}
                options={renewableType}
              />
            </Form.Item>
          </Col>
          <Form.Item
            name={RenewableKey.CAPACITY_UNIT}
            initialValue={
              FilteredUnit(formSelectedCountry ?? selectedCountry).electricity
            }
            hidden
            validateFirst
            hasFeedback
          ></Form.Item>
          <Col className="gutter-row">
            <Form.Item
              name={RenewableKey.TOTAL_CAPACITY}
              label={RenewableLabel.TOTAL_CAPACITY}
              rules={[
                {
                  pattern: RegEx.nonDecimal,
                  message: CommonErrors.nonDecimalNonNegative
                }
              ]}
              initialValue={null}
              labelCol={{span: 24}}
              validateFirst
              hasFeedback
            >
              <NumberInput
                addonAfter={
                  FilteredUnit(formSelectedCountry ?? selectedCountry)
                    .electricity
                }
                placeholder={`Enter ${RenewableLabel.TOTAL_CAPACITY}`}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item
              name={RenewableKey.PROVIDER_NAME}
              label={RenewableLabel.PROVIDER_NAME}
              rules={[
                {
                  pattern: RegEx.fullName,
                  message: CommonErrors.noSpecialChar
                }
              ]}
              initialValue={''}
              labelCol={{span: 24}}
              validateFirst
              hasFeedback
            >
              <Input
                onInput={(e) => {
                  const target = e.target as HTMLInputElement;
                  target.value = eachWordCap(target.value);
                }}
                placeholder={`Enter ${RenewableLabel.PROVIDER_NAME}`}
              />
            </Form.Item>
          </Col>
        </>
      )}
      {(currentStep === 2 || defaultDisplayStep) && (
        <>
          <MonthlyYearlyDrawerForm
            config={monYear}
            data={getFieldValue(TF) as MYData[]}
          />
        </>
      )}
    </>
  );
};

export default RenewableForm;
